import { debounce, Icon, IconButton } from "@material-ui/core";
import { FC, useCallback, useEffect, useState } from "react";
import { CollectionUtil, DateUtil, StringUtil, ValidateUtil } from "src/framework/commons";
import { ArrayUtil } from "src/framework/commons/ArrayUtil";
import { parseCEP } from "src/framework/commons/masks";
import { InputComboBoxList, InputDate, InputText } from "src/framework/components/forms";
import { InputDateMonthYear } from "src/framework/components/forms/inputs/InputDateMonthYear";
import { IDataResponse, STATUS_CODE } from "src/framework/providers/api";
import { notifierStore } from "src/framework/store";
import { useObservable } from "src/framework/store/storeUtil";
import { cepsProvider } from "src/providers/ceps/CepsProvider";
import { cidades$, estados$ } from "src/store/global.store";
import { Cidade, PedidoType } from "src/types";
import { FINANCEIRA_BV_ID, SITUACOES_RESIDENCIA } from "../constants";
import { IPropostaEnderecoRequestDom } from "../types";
import { errorsList$, tipoProposta$ } from "../../negociacao/Financiamento";
import { isValidCEP } from "@brazilian-utils/brazilian-utils";
import { PedidoStore } from "src/store/vendas/PedidoStore";
import { PedidosProvider } from "src/providers/pagamentos/PedidoProvider";

interface FormEnderecoProperties {
    dadosEndereco: any;
    isPessoaFisica?: boolean;
    isAvalista?: boolean;
    isConjuge?: boolean;
    indexAvalista?: number;
    isValid?: (value: boolean) => void;
    readonly?: boolean;
    onChangeData: (dados: any) => void;
    errorsList?: Array<string>;
    dataNascimento: string | null;
    formProfissao?: boolean;
    financeira_id: number;
    isEmpresa: boolean; 
}


const FormEndereco: FC<FormEnderecoProperties> = ({
    dadosEndereco,
    isValid,
    isPessoaFisica = false,
    isAvalista = false,
    isConjuge = false,
    readonly = false,
    onChangeData,
    errorsList = [],
    indexAvalista,
    dataNascimento,
    formProfissao,
    financeira_id,
    isEmpresa,
}) => {

    /** States e propriedades **/
    const estadosListState: any = useObservable(estados$);
    const cidadesListState = useObservable(cidades$);

    const pedidoCodigoRedirect = useObservable<string | null | undefined>(PedidoStore.getPedidoCodigoRedirectSubject());

    const [propriedadeResidenciaState, setPropriedadeResidenciaState] = useState<any>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [estado, setEstado] = useState<any>(null);
    const [cidade, setCidade] = useState<any>(null);
    const [disabledFields, setDisabledFields] = useState<boolean>(true)
    const [disabledErrorDataInicioEndereco, setDisabledErrorDataInicioEndereco] = useState<boolean>(false)

    const handleValidChange = () => {
        if (isValid) {
        }
    }

    const searchCEP = async (value: string): Promise<IDataResponse> => {
        try {
            setLoading(true)
            const response = await cepsProvider.getCep(value);

            if (response.statusCode !== STATUS_CODE.OK) {
                notifierStore.setNotifier({
                    title: "Aviso",
                    content: ["CEP não encontrado!"],
                    timeOut: 3500,
                    onClose: () => {
                        notifierStore.setOpen(false)
                        setDisabledFields(false);
                    }
                })
                notifierStore.setOpen(true)

            }
            return response;
        } catch (error) {
            return {
                data: null,
                message: "",
                statusCode: STATUS_CODE.BAD_REQUEST
            }
        } finally {
            setLoading(false)
        }

    }

    const handleCep = async (cep: string | null): Promise<void> => {
        if (cep) {
            const _cleanedValue = cep.replace(/[^0-9]/g, "");
            if (_cleanedValue?.length > 7) {
                const response = await searchCEP(_cleanedValue);
                const _state = CollectionUtil.merge({}, dadosEndereco)
                if (_state) {
                    if (response.statusCode === STATUS_CODE.OK) {
                        _state.bairro = response.data.bairro
                        _state.cep = response.data.cep
                        _state.logradouro = response.data.endereco
    
                        _state.estado = response.data.uf
                        const _estado = estados$.getValue()?.data?.find((e: any) => e.id === response?.data?.uf) || null
                        if (_estado) {
                            setEstado(_estado);
                            _state.estado = _estado.id
                        }
    
                        _state.cidade_ibge = response.data.ibge
                        const _cidade = cidades$.getValue()?.data?.find((c: Cidade) => c.id === response?.data?.ibge) || null
                        if (_cidade) {
                            setCidade(_cidade);
                            _state.cidade = _cidade.nome;
                        }
                        onChangeData(_state);
                    }
                }
            }
        }
    }

    /**  Effects **/
    useEffect(() => {
        const _propriedadeResidencia = SITUACOES_RESIDENCIA.find((pr) => pr.id === dadosEndereco?.situacao) || null
        setPropriedadeResidenciaState(_propriedadeResidencia)
    }, [dadosEndereco?.situacao]);

    useEffect(() => {
        const _estado = estados$.getValue()?.data?.find((e: any) => e.id === dadosEndereco?.estado) || null
        setEstado(_estado)
    }, [dadosEndereco?.estado]);

    useEffect(() => {
        const _cidade = cidades$.getValue()?.data?.find((c: any) => c.nome === dadosEndereco?.cidade) || null
        setCidade(_cidade)
    }, [dadosEndereco?.cidade]);

    useEffect(() => {
        return () => {
            setCidade(null)
            setEstado(null)
            setPropriedadeResidenciaState(null)
        }
    }, [])

    return <div className="FormEndereco">
        <div className="row">
            <div className="X12">
                <h4>Dados endereço</h4>
            </div>
        </div>
        <div className="row">
            <div className="X12 M6">
                <InputText
                    label="CEP"
                    disabled={readonly}
                    fullWidth
                    mask="cep"
                    value={parseCEP(`${dadosEndereco?.cep || ""}`)}
                    helperText={<>Clique na <Icon style={{fontSize: "0.85rem"}}>search</Icon> para consultar um CEP</>}
                    sufix={<>
                        <IconButton 
                            size="small"
                            onClick={async () => {
                                if (pedidoCodigoRedirect) {
                                    const pedidosProvider = new PedidosProvider();
                                    const responsePedido: IDataResponse<PedidoType[]> = await pedidosProvider.getPedidos(pedidoCodigoRedirect);
                                    if (responsePedido.statusCode !== STATUS_CODE.OK) return;
                                }
                                const _state = CollectionUtil.merge({}, dadosEndereco)
                                handleCep(_state?.cep)
                            }}
                        >
                            <Icon>search</Icon>
                        </IconButton>
                    </>}
                    onChange={(event: any) => {
                        const _state = CollectionUtil.merge({}, dadosEndereco)
                        const _value: any = event.target.value;
                        const customValue = _value?.replace(/\D/g, "") || '';

                        if (_state) {
                            _state.cep = customValue
                            onChangeData(_state);
                            return;
                        }
                    }}
                    error={
                        (!isAvalista && !isConjuge && ArrayUtil.contains(errorsList, "pessoa_fisica#endereco.cep")) 
                        || (!isAvalista && isConjuge && ArrayUtil.contains(errorsList, "pessoa_fisica.conjuge#endereco.cep")) 
                        || (isAvalista && !isConjuge && ArrayUtil.contains(errorsList, `avalistas#${indexAvalista}#endereco.cep`))
                        || (isAvalista && isConjuge && ArrayUtil.contains(errorsList, `avalistas#${indexAvalista}#conjuge.endereco.cep`)) 
                        || (!isPessoaFisica && !isAvalista && !isConjuge && ArrayUtil.contains(errorsList, `pessoa_juridica#endereco.cep`)) 
                    }
                    onBlur={()=>{
                        if(!dadosEndereco?.cep/* || !isValidCEP(dadosEndereco?.cep)*/){
                            if(isConjuge && !isAvalista){
                                errorsList$.next([...errorsList, "pessoa_fisica.conjuge#endereco.cep"])
                            }else if(!isConjuge && isAvalista){
                                errorsList$.next([...errorsList, `avalistas#${indexAvalista}#endereco.cep`])
                            }else if(isConjuge && isAvalista){
                                errorsList$.next([...errorsList, `avalistas#${indexAvalista}#conjuge.endereco.cep`])
                            }
                        }else{
                            if(isConjuge && !isAvalista){
                                errorsList$.next(errorsList.filter((error: any)=>error !== `pessoa_fisica.conjuge#endereco.cep`))
                            }else if(!isConjuge && isAvalista){
                                errorsList$.next(errorsList.filter((error: any)=>error !== `avalistas#${indexAvalista}#endereco.cep`))
                            }else if(isConjuge && isAvalista){
                                errorsList$.next(errorsList.filter((error: any)=>error !== `avalistas#${indexAvalista}#conjuge.endereco.cep`))
                            }
                        }
                    }}
                />
            </div>

            <div className="X12 M6">
                <InputText
                    disabled={readonly || loading}
                    fullWidth
                    label="Logradouro"
                    maxLength={60}
                    value={dadosEndereco?.logradouro?.normalize('NFD')?.replace(/([\u0300-\u036f]|[^0-9a-zA-Z- ])/g, '')}
                    onChange={(event: any) => {
                        const _state = CollectionUtil.merge({}, dadosEndereco)
                        if (_state) {
                            _state.logradouro = event.target.value
                            onChangeData(_state)
                        }
                    }}
                    error={
                        (!isAvalista && !isConjuge && ArrayUtil.contains(errorsList, "pessoa_fisica#endereco.logradouro")) 
                        || (!isAvalista && isConjuge && ArrayUtil.contains(errorsList, "pessoa_fisica.conjuge#endereco.logradouro")) 
                        || (isAvalista && !isConjuge && ArrayUtil.contains(errorsList, `avalistas#${indexAvalista}#endereco.logradouro`))
                        || (isAvalista && isConjuge && ArrayUtil.contains(errorsList, `avalistas#${indexAvalista}#conjuge.endereco.logradouro`)) 
                        || (!isPessoaFisica && !isAvalista && !isConjuge && ArrayUtil.contains(errorsList, `pessoa_juridica#endereco.logradouro`))
                    }
                    onBlur={()=>{
                        if(!dadosEndereco?.logradouro){
                            if(isConjuge && !isAvalista){
                                errorsList$.next([...errorsList, "pessoa_fisica.conjuge#endereco.logradouro"])
                            }else if(!isConjuge && isAvalista){
                                errorsList$.next([...errorsList, `avalistas#${indexAvalista}#endereco.logradouro`])
                            }else if(isConjuge && isAvalista){
                                errorsList$.next([...errorsList, `avalistas#${indexAvalista}#conjuge.endereco.logradouro`])
                            }
                        }else{
                            if(isConjuge && !isAvalista){
                                errorsList$.next(errorsList.filter((error: any)=>error !== `pessoa_fisica.conjuge#endereco.logradouro`))
                            }else if(!isConjuge && isAvalista){
                                errorsList$.next(errorsList.filter((error: any)=>error !== `avalistas#${indexAvalista}#endereco.logradouro`))
                            }else if(isConjuge && isAvalista){
                                errorsList$.next(errorsList.filter((error: any)=>error !== `avalistas#${indexAvalista}#conjuge.endereco.logradouro`))
                            }
                        }
                    }}
                />
            </div>

            <div className="X12 M2">
                <InputText
                    disabled={readonly || loading}
                    fullWidth
                    label="Número"
                    value={dadosEndereco?.numero}
                    onChange={(event: any) => {
                        const _state = CollectionUtil.merge({}, dadosEndereco)
                        if (_state) {
                            _state.numero = (event.target.value)?.replace(/[^0-9]/g, '')
                            onChangeData(_state)
                        }
                        handleValidChange();
                    }}
                    error={
                        (!isAvalista && !isConjuge && ArrayUtil.contains(errorsList, "pessoa_fisica#endereco.numero")) 
                        || (!isAvalista && isConjuge && ArrayUtil.contains(errorsList, "pessoa_fisica.conjuge#endereco.numero")) 
                        || (isAvalista && !isConjuge && ArrayUtil.contains(errorsList, `avalistas#${indexAvalista}#endereco.numero`))
                        || (isAvalista && isConjuge && ArrayUtil.contains(errorsList, `avalistas#${indexAvalista}#conjuge.endereco.numero`)) 
                        || (!isPessoaFisica && !isAvalista && !isConjuge && ArrayUtil.contains(errorsList, `pessoa_juridica#endereco.numero`))
                    }
                    onBlur={()=>{
                        if(!dadosEndereco?.numero){
                            if(isConjuge && !isAvalista){
                                errorsList$.next([...errorsList, "pessoa_fisica.conjuge#endereco.numero"])
                            }else if(!isConjuge && isAvalista){
                                errorsList$.next([...errorsList, `avalistas#${indexAvalista}#endereco.numero`])
                            }else if(isConjuge && isAvalista){
                                errorsList$.next([...errorsList, `avalistas#${indexAvalista}#conjuge.endereco.numero`])
                            }
                        }else{
                            if(isConjuge && !isAvalista){
                                errorsList$.next(errorsList.filter((error: any)=>error !== `pessoa_fisica.conjuge#endereco.numero`))
                            }else if(!isConjuge && isAvalista){
                                errorsList$.next(errorsList.filter((error: any)=>error !== `avalistas#${indexAvalista}#endereco.numero`))
                            }else if(isConjuge && isAvalista){
                                errorsList$.next(errorsList.filter((error: any)=>error !== `avalistas#${indexAvalista}#conjuge.endereco.numero`))
                            }
                        }
                    }}
                />
            </div>

            <div className="X12 M4">
                <InputText
                    disabled={readonly || loading}
                    fullWidth
                    label="Complemento"
                    value={dadosEndereco?.complemento}
                    maxLength={15}
                    onChange={(event: any) => {
                        const _state = CollectionUtil.merge({}, dadosEndereco)
                        if (_state) {
                            const valor = event.target.value;
                            _state.complemento = valor.replace(/[^a-zA-Z0-9 ]/g, "").slice(0, 15) || null;
                            onChangeData(_state)
                        }
                        handleValidChange();

                    }}
                    onBlur={()=>{
                        //faz o trim() somente quando sai do campo
                        const state = CollectionUtil.merge({}, dadosEndereco);
                        if(state.complemento){
                            state.complemento = state.complemento.trim();
                            onChangeData(state);
                        }
                    }}
                    // error={ArrayUtil.contains(errorsList, "pessoa_fisica#endereco.complemento") || ArrayUtil.contains(errorsList, "pessoa_fisica.conjuge#endereco.complemento") || ArrayUtil.contains(errorsList, `avalistas#${indexAvalista}#conjuge.endereco.complemento`) || ArrayUtil.contains(errorsList, `avalistas#${indexAvalista}#endereco.complemento`)}
                />
            </div>

            <div className="X12 M6">
                <InputText
                    disabled={readonly || loading}
                    fullWidth
                    label="Bairro"
                    value={dadosEndereco?.bairro?.normalize('NFD').replace(/([\u0300-\u036f]|[^0-9a-zA-Z- ])/g, '')}
                    onChange={(event: any) => {
                        const _state = CollectionUtil.merge({}, dadosEndereco)
                        if (_state) {
                            _state.bairro = event.target.value
                            onChangeData(_state)
                        }
                        handleValidChange();
                    }}
                    error={
                        (!isAvalista && !isConjuge && ArrayUtil.contains(errorsList, "pessoa_fisica#endereco.bairro")) 
                        || (!isAvalista && isConjuge && ArrayUtil.contains(errorsList, "pessoa_fisica.conjuge#endereco.bairro")) 
                        || (isAvalista && !isConjuge && ArrayUtil.contains(errorsList, `avalistas#${indexAvalista}#endereco.bairro`))
                        || (isAvalista && isConjuge && ArrayUtil.contains(errorsList, `avalistas#${indexAvalista}#conjuge.endereco.bairro`)) 
                        || (!isPessoaFisica && !isAvalista && !isConjuge && ArrayUtil.contains(errorsList, `pessoa_juridica#endereco.bairro`))
                    }
                    onBlur={()=>{
                        if(!dadosEndereco?.bairro){
                            if(isConjuge && !isAvalista){
                                errorsList$.next([...errorsList, "pessoa_fisica.conjuge#endereco.bairro"])
                            }else if(!isConjuge && isAvalista){
                                errorsList$.next([...errorsList, `avalistas#${indexAvalista}#endereco.bairro`])
                            }else if(isConjuge && isAvalista){
                                errorsList$.next([...errorsList, `avalistas#${indexAvalista}#conjuge.endereco.bairro`])
                            }
                        }else{
                            if(isConjuge && !isAvalista){
                                errorsList$.next(errorsList.filter((error: any)=>error !== `pessoa_fisica.conjuge#endereco.bairro`))
                            }else if(!isConjuge && isAvalista){
                                errorsList$.next(errorsList.filter((error: any)=>error !== `avalistas#${indexAvalista}#endereco.bairro`))
                            }else if(isConjuge && isAvalista){
                                errorsList$.next(errorsList.filter((error: any)=>error !== `avalistas#${indexAvalista}#conjuge.endereco.bairro`))
                            }
                        }
                    }}
                />
            </div>

            <div className="X12 M6">
                <InputComboBoxList
                    label="Cidade"
                    textField="nome"
                    valueField="id"
                    disabled={readonly || loading || estado === undefined || estado === null || disabledFields}
                    value={cidade}
                    options={cidadesListState?.data?.filter((c: Cidade) => c.estado_id === estado?.id) || []}
                    onChange={(_cidade) => {
                        const _state = CollectionUtil.merge({}, dadosEndereco)
                        if (_state) {
                            _state.cidade = _cidade?.nome || null
                            _state.cidade_ibge = _cidade?.ibge || null
                            onChangeData(_state)
                        }
                        handleValidChange();
                    }}
                    error={
                        (!isAvalista && !isConjuge && ArrayUtil.contains(errorsList, "pessoa_fisica#endereco.cidade")) 
                        || (!isAvalista && isConjuge && ArrayUtil.contains(errorsList, "pessoa_fisica.conjuge#endereco.cidade",true)) 
                        || (isAvalista && !isConjuge && ArrayUtil.contains(errorsList, `avalistas#${indexAvalista}#endereco.cidade`,true))
                        || (isAvalista && isConjuge && ArrayUtil.contains(errorsList, `avalistas#${indexAvalista}#conjuge.endereco.cidade`,true)) 
                        || (!isPessoaFisica && !isAvalista && !isConjuge && ArrayUtil.contains(errorsList, `pessoa_juridica#endereco.cidade`))
                    }
                    onBlur={()=>{
                        if(!dadosEndereco?.cidade){
                            if(isConjuge && !isAvalista){
                                errorsList$.next([...errorsList, "pessoa_fisica.conjuge#endereco.cidade"])
                            }else if(!isConjuge && isAvalista){
                                errorsList$.next([...errorsList, `avalistas#${indexAvalista}#endereco.cidade`])
                            }else if(isConjuge && isAvalista){
                                errorsList$.next([...errorsList, `avalistas#${indexAvalista}#conjuge.endereco.cidade`])
                            }
                        }else{
                            if(isConjuge && !isAvalista){
                                errorsList$.next(errorsList.filter((error: any)=>error !== `pessoa_fisica.conjuge#endereco.cidade`))
                            }else if(!isConjuge && isAvalista){
                                errorsList$.next(errorsList.filter((error: any)=>error !== `avalistas#${indexAvalista}#endereco.cidade`))
                            }else if(isConjuge && isAvalista){
                                errorsList$.next(errorsList.filter((error: any)=>error !== `avalistas#${indexAvalista}#conjuge.endereco.cidade`))
                            }
                        }
                    }}
                />
            </div>

            <div className="X12 M6">
                <InputComboBoxList
                    disabled={readonly || loading || disabledFields}
                    label="Estado"
                    textField="nome"
                    valueField="ibge"
                    value={estado}
                    options={estadosListState?.data || []}
                    renderOptions={(option) => {
                        return `${option.id} - ${option.nome}`
                    }}
                    renderOptionsLabel={(option) => {
                        return `${option.id}`
                    }}
                    onChange={(_estado) => {
                        const _state = CollectionUtil.merge({}, dadosEndereco)
                        if (_state) {
                            _state.estado = _estado?.id || undefined
                            if (!_estado) {
                                setCidade(null)
                            }
                            onChangeData(_state)
                        }
                        handleValidChange();
                    }}
                    error={
                        (!isAvalista && !isConjuge && ArrayUtil.contains(errorsList, "pessoa_fisica#endereco.estado")) 
                        || (!isAvalista && isConjuge && ArrayUtil.contains(errorsList, "pessoa_fisica.conjuge#endereco.estado")) 
                        || (isAvalista && !isConjuge && ArrayUtil.contains(errorsList, `avalistas#${indexAvalista}#endereco.estado`))
                        || (isAvalista && isConjuge && ArrayUtil.contains(errorsList, `avalistas#${indexAvalista}#conjuge.endereco.estado`)) 
                        || (!isPessoaFisica && !isAvalista && !isConjuge && ArrayUtil.contains(errorsList, `pessoa_juridica#endereco.estado`))
                    }
                    onBlur={()=>{
                        if(!dadosEndereco?.estado){
                            if(isConjuge && !isAvalista){
                                errorsList$.next([...errorsList, "pessoa_fisica.conjuge#endereco.estado"])
                            }else if(!isConjuge && isAvalista){
                                errorsList$.next([...errorsList, `avalistas#${indexAvalista}#endereco.estado`])
                            }else if(isConjuge && isAvalista){
                                errorsList$.next([...errorsList, `avalistas#${indexAvalista}#conjuge.endereco.estado`])
                            }
                        }else{
                            if(isConjuge && !isAvalista){
                                errorsList$.next(errorsList.filter((error: any)=>error !== `pessoa_fisica.conjuge#endereco.estado`))
                            }else if(!isConjuge && isAvalista){
                                errorsList$.next(errorsList.filter((error: any)=>error !== `avalistas#${indexAvalista}#endereco.estado`))
                            }else if(isConjuge && isAvalista){
                                errorsList$.next(errorsList.filter((error: any)=>error !== `avalistas#${indexAvalista}#conjuge.endereco.estado`))
                            }
                        }
                    }}
                />
            </div>

            <div className="X12 M6">
                <InputComboBoxList
                    disabled={readonly || loading}
                    label="Situação imóvel"
                    textField="nome"
                    valueField="id"
                    value={propriedadeResidenciaState}
                    options={SITUACOES_RESIDENCIA}
                    onChange={(_situacao_residencia) => {
                        const _state = CollectionUtil.merge({}, dadosEndereco)
                        _state.situacao = _situacao_residencia?.id || null
                        onChangeData(_state)
                        handleValidChange();
                    }}
                    onBlur={() => {
                        if (!propriedadeResidenciaState) {
                            if(isConjuge && !isAvalista){
                                errorsList$.next([...errorsList, "pessoa_fisica.conjuge#endereco.situacao"])
                            }else if(!isConjuge && isAvalista){
                                errorsList$.next([...errorsList, `avalistas#${indexAvalista}#endereco.situacao`])
                            }else if(isConjuge && isAvalista){
                                errorsList$.next([...errorsList, `avalistas#${indexAvalista}#conjuge.endereco.situacao`])
                            }else{
                                errorsList$.next([...errorsList$.getValue(), "pessoa_juridica#endereco.situacao", "pessoa_fisica#endereco.situacao"])
                            }
                        } else {
                            if(isConjuge && !isAvalista){
                                errorsList$.next(errorsList.filter((error: any)=>error !== `pessoa_fisica.conjuge#endereco.situacao`))
                            }else if(!isConjuge && isAvalista){
                                errorsList$.next(errorsList.filter((error: any)=>error !== `avalistas#${indexAvalista}#endereco.situacao`))
                            }else if(isConjuge && isAvalista){
                                errorsList$.next(errorsList.filter((error: any)=>error !== `avalistas#${indexAvalista}#conjuge.endereco.situacao`))
                            }else{
                                errorsList$.next(errorsList$.getValue().filter((item: string) => {
                                    return item !== "pessoa_juridica#endereco.situacao" && item !== "pessoa_fisica#endereco.situacao"
                                }))
                            }
                        }
                    }}
                    error={
                        (!isAvalista && !isConjuge && ArrayUtil.contains(errorsList, "pessoa_fisica#endereco.situacao")) 
                        || (!isAvalista && isConjuge && ArrayUtil.contains(errorsList, "pessoa_fisica.conjuge#endereco.situacao")) 
                        || (isAvalista && !isConjuge && ArrayUtil.contains(errorsList, `avalistas#${indexAvalista}#endereco.situacao`))
                        || (isAvalista && isConjuge && ArrayUtil.contains(errorsList, `avalistas#${indexAvalista}#conjuge.endereco.situacao`)) 
                        || (!isPessoaFisica && !isAvalista && !isConjuge && ArrayUtil.contains(errorsList, `pessoa_juridica#endereco.situacao`))
                        || !propriedadeResidenciaState
                    }
                />
            </div>

            <div className="X12 M6">
                <InputDate 
                    fullWidth
                    disabled={readonly}
                    label={`Neste endereço desde`}
                    value={(dadosEndereco as IPropostaEnderecoRequestDom)?.data_inicio_endereco || null}
                    format="dd/MM/yyyy"
                    maxDate={new Date()}
                    helperText={((isPessoaFisica && !isAvalista && !isConjuge && ArrayUtil.contains(errorsList, !!!formProfissao ?  "pessoa_fisica#endereco.data_inicio_endereco_invalida" : "pessoa_fisica.ocupacao#endereco.data_inicio_endereco_invalida")) 
                        || (isPessoaFisica && !isAvalista && isConjuge && ArrayUtil.contains(errorsList, !!!formProfissao ? "pessoa_fisica.conjuge#endereco.data_inicio_endereco_invalida" : "pessoa_fisica.conjuge#ocupacao.endereco.data_inicio_endereco_invalida")) 
                        || (isPessoaFisica && isAvalista && !isConjuge && ArrayUtil.contains(errorsList, !!!formProfissao ? `avalistas#${indexAvalista}#endereco.data_inicio_endereco_invalida` : `avalistas#${indexAvalista}#ocupacao.endereco.data_inicio_endereco_invalida`))
                        || (isPessoaFisica && isAvalista && isConjuge && ArrayUtil.contains(errorsList, !!!formProfissao ? `avalistas#${indexAvalista}#conjuge.endereco.data_inicio_endereco_invalida` : `avalistas#${indexAvalista}#conjuge.ocupacao.endereco.data_inicio_endereco_invalida`))
                        || (!isPessoaFisica && !isAvalista && !isConjuge && ArrayUtil.contains(errorsList, "pessoa_juridica#endereco.data_inicio_endereco_invalida")))
                        && !disabledErrorDataInicioEndereco
                        ? (!isEmpresa ? "A data preenchida precisa ser superior a data de nascimento." : `A data preenchida precisa ser superior a “Data de Fundação” da empresa.`) : undefined}
                    onChange={(newDate) => {
                        const _state: IPropostaEnderecoRequestDom = CollectionUtil.merge({}, dadosEndereco)
                        if (_state) {
                            const value = newDate;
 
                            const _initialDate = new Date(value || "").getTime();
                            const _finalDate = new Date(dataNascimento || "").getTime();
                            
                            console.log(">>>9", _initialDate, _finalDate, _initialDate < _finalDate);
                            console.log(">>>10", errorsList);

                            if(_initialDate < _finalDate){

                                if(isPessoaFisica && !isAvalista && !isConjuge) {
                                    errorsList$.next([...errorsList, !!!formProfissao ?  "pessoa_fisica#endereco.data_inicio_endereco_invalida" : "pessoa_fisica.ocupacao#endereco.data_inicio_endereco_invalida"])
                                }

                                if(isPessoaFisica && !isAvalista && isConjuge) {
                                    errorsList$.next([...errorsList, !!!formProfissao ?  `pessoa_fisica.conjuge#endereco.data_inicio_endereco_invalida` : "pessoa_fisica.conjuge#ocupacao.endereco.data_inicio_endereco_invalida"])
                                }

                                if(isPessoaFisica && isAvalista && !isConjuge) {
                                    errorsList$.next([...errorsList, !!!formProfissao ? `avalistas#${indexAvalista}#endereco.data_inicio_endereco_invalida` : `avalistas#${indexAvalista}#ocupacao.endereco.data_inicio_endereco_invalida`])
                                }

                                if(isPessoaFisica && isAvalista && isConjuge) {
                                    errorsList$.next([...errorsList, !!!formProfissao ? `avalistas#${indexAvalista}#conjuge.endereco.data_inicio_endereco_invalida` : `avalistas#${indexAvalista}#conjuge.ocupacao.endereco.data_inicio_endereco_invalida`])
                                }
                                console.log(">>>555", !isPessoaFisica && !isAvalista && !isConjuge, financeira_id);
                                if(!isPessoaFisica && !isAvalista && !isConjuge && financeira_id === FINANCEIRA_BV_ID) {
                                    console.log(">>>55");
                                    errorsList$.next([...errorsList, `pessoa_juridica#endereco.data_inicio_endereco_invalida`])
                                }
                                
                                /*
                                notifierStore.setNotifier({
                                    title: "Aviso",
                                    content: "A data preenchida precisa ser superior a data de nascimento.",
                                    timeOut: 3500,
                                    onClose: () => {
                                        notifierStore.setOpen(false)
                                    }
                                })
                                notifierStore.setOpen(true)
                                */
                                setDisabledErrorDataInicioEndereco(false);
                
                            }else{
                                setDisabledErrorDataInicioEndereco(true);
                            }

                            if(_initialDate >= _finalDate) {
                                let filtro = "";

                                if(isPessoaFisica && isAvalista && !isConjuge) {
                                    filtro = !!!formProfissao ? `avalistas#${indexAvalista}#endereco.data_inicio_endereco_invalida` : `avalistas#${indexAvalista}#ocupacao.endereco.data_inicio_endereco_invalida`;
                                    console.log(">>>11", filtro, errorsList.filter((error: any)=>error !== filtro));
                                    errorsList$.next(errorsList.filter((error: any)=>error !== filtro))
                                }

                                if(isPessoaFisica && isAvalista && isConjuge) {
                                    filtro = !!!formProfissao ? `avalistas#${indexAvalista}#conjuge.endereco.data_inicio_endereco_invalida` : `avalistas#${indexAvalista}#conjuge.ocupacao.endereco.data_inicio_endereco_invalida`;
                                    errorsList$.next(errorsList.filter((error: any)=>error !== filtro))
                                }
                                console.log(">>>555", !isPessoaFisica && !isAvalista && !isConjuge, financeira_id);
                                if(!isPessoaFisica && !isAvalista && !isConjuge && financeira_id === FINANCEIRA_BV_ID) {
                                    console.log(">>>55");
                                    errorsList$.next(errorsList.filter((error: any)=>error !==  `pessoa_juridica#endereco.data_inicio_endereco_invalida`))
                                }
                            }
                            
                            console.log(">>>", value);
                            _state.data_inicio_endereco = value;
                            onChangeData(_state)
                        }
                    }}
                    error={
                        ((!isAvalista && !isConjuge && ArrayUtil.contains(errorsList, !!!formProfissao ?  "pessoa_fisica#endereco.data_inicio_endereco" : "pessoa_fisica.ocupacao#endereco.data_inicio_endereco")) 
                        || (!isAvalista && isConjuge && ArrayUtil.contains(errorsList, !!!formProfissao ? "pessoa_fisica.conjuge#endereco.data_inicio_endereco" : "pessoa_fisica.conjuge#ocupacao.endereco.data_inicio_endereco")) 
                        || (isAvalista && !isConjuge && ArrayUtil.contains(errorsList, !!!formProfissao ? `avalistas#${indexAvalista}#endereco.data_inicio_endereco` : `avalistas#${indexAvalista}#ocupacao.endereco.data_inicio_endereco`))
                        || (isAvalista && isConjuge && ArrayUtil.contains(errorsList, !!!formProfissao ? `avalistas#${indexAvalista}#conjuge.endereco.data_inicio_endereco` : `avalistas#${indexAvalista}#conjuge.ocupacao.endereco.data_inicio_endereco`)) 
                        || (!isPessoaFisica && !isAvalista && !isConjuge && ArrayUtil.contains(errorsList, `pessoa_juridica#endereco.data_inicio_endereco`)))
                        && !disabledErrorDataInicioEndereco
                    }
                    onBlur={()=>{

                        if(isPessoaFisica || isConjuge || isAvalista || !isPessoaFisica) {
                            const _initialDate = new Date(dadosEndereco?.data_inicio_endereco || "").getTime();
                            const _finalDate = new Date(dataNascimento || "").getTime();
                            /*
                            if(_initialDate < _finalDate){
                                errorsList$.next([...errorsList, `data_inicio_endereco_invalida`])
             
                                return
                            }
                            */

                            console.log(">>>10", errorsList, dadosEndereco?.data_inicio_endereco && !isNaN(new Date(dadosEndereco?.data_inicio_endereco).getTime()));

                            if(dadosEndereco?.data_inicio_endereco && !isNaN(new Date(dadosEndereco?.data_inicio_endereco).getTime())){
                                console.log(">>>10", ArrayUtil.removeItem(errorsList, `data_nascimaneto_invalida`));
                                errorsList$.next(ArrayUtil.removeItem(errorsList, `data_nascimaneto_invalida`));

                            }
                        }

                        if(!dadosEndereco?.data_inicio_endereco){
                            if(isConjuge && !isAvalista){
                                errorsList$.next([...errorsList, "pessoa_fisica.conjuge#endereco.data_inicio_endereco"])
                            }else if(!isConjuge && isAvalista){
                                errorsList$.next([...errorsList, `avalistas#${indexAvalista}#endereco.data_inicio_endereco`])
                            }else if(isConjuge && isAvalista){
                                errorsList$.next([...errorsList, `avalistas#${indexAvalista}#conjuge.endereco.data_inicio_endereco`])
                            }
                        }else{
                            if(isConjuge && !isAvalista){
                                errorsList$.next(errorsList.filter((error: any)=>error !== `pessoa_fisica.conjuge#endereco.data_inicio_endereco`))
                            }else if(!isConjuge && isAvalista){
                                errorsList$.next(errorsList.filter((error: any)=>error !== `avalistas#${indexAvalista}#endereco.data_inicio_endereco`))
                            }else if(isConjuge && isAvalista){
                                errorsList$.next(errorsList.filter((error: any)=>error !== `avalistas#${indexAvalista}#conjuge.endereco.data_inicio_endereco`))
                            }
                        }
                    }}
                />
            </div>
        </div>

    </div>
}

export { FormEndereco }
